import React, { useState, useEffect } from 'react';
import { Search, MapPin, Phone, Globe, Star, Trash2, ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';

export default function AdminImportedDoctors() {
  const { api } = useAuth();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [deleting, setDeleting] = useState(null);

  useEffect(() => { fetchDoctors(); }, [page]);

  const fetchDoctors = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/doctors?page=${page}&limit=50`);
      setDoctors(res.data.doctors || []);
      setTotalPages(res.data.total_pages);
      setTotal(res.data.total);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (doctorId, name) => {
    if (!window.confirm(`Remover "${name}" do banco de dados?`)) return;
    setDeleting(doctorId);
    try {
      await api.delete(`/admin/doctors/${doctorId}`);
      setDoctors(prev => prev.filter(d => d.id !== doctorId));
      setTotal(prev => prev - 1);
    } catch (e) {
      alert('Erro ao remover: ' + (e.response?.data?.detail || e.message));
    } finally {
      setDeleting(null);
    }
  };

  const filtered = search
    ? doctors.filter(d => {
        const s = search.toLowerCase();
        return (d.full_name || '').toLowerCase().includes(s)
          || (d.city || '').toLowerCase().includes(s)
          || (d.specialty_name || '').toLowerCase().includes(s);
      })
    : doctors;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-imported-doctors">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" data-testid="page-title">Médicos Importados</h1>
            <p className="text-slate-500 text-sm mt-1">{total} registros no banco de dados</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => { setPage(1); fetchDoctors(); }} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Atualizar
          </Button>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Filtrar por nome, cidade ou especialidade..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 h-10 bg-white"
            data-testid="search-filter"
          />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="doctors-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">#</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Nome</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Especialidade</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Cidade/UF</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Telefone</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Avaliacao</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Convênios</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Origem</th>
                  <th className="px-4 py-3 text-left font-medium text-slate-600">Acoes</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400">Carregando...</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={9} className="px-4 py-12 text-center text-slate-400">Nenhum m?dico encontrado</td></tr>
                ) : filtered.map((d, idx) => (
                  <tr key={d.id} className="border-b border-slate-50 hover:bg-slate-50/50" data-testid={`doctor-row-${d.id}`}>
                    <td className="px-4 py-3 text-slate-400">{(page - 1) * 50 + idx + 1}</td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900 whitespace-nowrap">{d.full_name}</p>
                      {d.address && <p className="text-xs text-slate-400 truncate max-w-[200px]">{d.address}</p>}
                    </td>
                    <td className="px-4 py-3">
                      {d.specialty_name ? (
                        <Badge variant="secondary" className="text-xs">{d.specialty_name}</Badge>
                      ) : <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {d.city || d.state ? (
                        <span className="flex items-center gap-1 text-slate-600">
                          <MapPin className="w-3.5 h-3.5 text-slate-400" />
                          {d.city}{d.state ? ` - ${d.state}` : ''}
                        </span>
                      ) : <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {d.phone ? (
                        <span className="flex items-center gap-1 text-slate-600">
                          <Phone className="w-3.5 h-3.5 text-slate-400" />
                          {d.phone}
                        </span>
                      ) : <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {d.rating_avg > 0 ? (
                        <span className="flex items-center gap-1">
                          <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                          <span className="font-medium">{Number(d.rating_avg).toFixed(1)}</span>
                          <span className="text-slate-400">({d.rating_count})</span>
                        </span>
                      ) : <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3">
                      {d.accepted_health_plans?.length > 0 ? (
                        <span className="text-xs text-slate-600">{d.accepted_health_plans.length} plano(s)</span>
                      ) : <span className="text-slate-300">-</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        {d.imported_from === 'google_maps' ? (
                          <Badge className="bg-blue-50 text-blue-700 border-0 text-xs">Maps</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600 border-0 text-xs">Manual</Badge>
                        )}
                        {d.maps_url && (
                          <a href={d.maps_url} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-primary">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </a>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50"
                        onClick={() => handleDelete(d.id, d.full_name)}
                        disabled={deleting === d.id}
                        data-testid={`delete-btn-${d.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-sm text-slate-500">P?gina {page} de {totalPages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Pr?xima</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
